import { FC } from 'react'
import { styled } from '../../../lib/stitches'
import { Box, Row } from '../../core/Components'
import { ArpeggiLightLogo } from '../../core/icons'
import Text from '../../core/Text/Text'
import { MINTABLE_COLORS_MAP } from '../../platform/color'
import { appear } from '../animations'

const LogoMonochrome = (props) => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 28 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M0 4C0 1.79086 1.79086 0 4 0H24C26.2091 0 28 1.79086 28 4V24C28 26.2091 26.2091 28 24 28H4C1.79086 28 0 26.2091 0 24V4Z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M24 28C26.2091 28 28 26.2091 28 24V4C28 1.79086 26.2091 0 24 0H4C1.79086 0 0 1.79086 0 4V24C0 26.2091 1.79086 28 4 28H24ZM16 2H11.9999V15.0556C11.9999 15.3318 11.776 15.5556 11.4999 15.5556H10.3328V26H17.8V15.5556H16.5C16.2239 15.5556 16 15.3318 16 15.0556V2ZM21.6 2V15.0556C21.6 15.3318 21.3761 15.5556 21.1 15.5556H19.8V26H24C25.1046 26 26 25.1046 26 24V4C26 2.89543 25.1046 2 24 2H21.6ZM4 2H6.3999V15.0556C6.3999 15.3318 6.62376 15.5556 6.8999 15.5556H8.33276V26H4C2.89543 26 2 25.1046 2 24V4C2 2.89543 2.89543 2 4 2Z"
      fill="#141414"
    />
  </svg>
)

const StyledLogo = styled(LogoMonochrome, {
  margin: '2px',
  width: '28px',
  height: '28px',
  cursor: 'pointer'
})

const StyledAvatar = styled('img', {
  width: '28px',
  height: '28px',
  overflow: 'hidden',
  borderRadius: '9999px',
  cursor: 'pointer',
  animation: `${appear} 500ms cubic-bezier(0.16, 1, 0.3, 1)`,
  transition: 'all 100ms ease',
  '&:active': {
    opacity: '.8',
    transform: 'translateY(1px)'
  }
})

const Avatar = StyledAvatar

const LinkAvatar: FC<{ avatarURL: string; artistURL: string }> = ({ avatarURL, artistURL }) => (
  <a href={artistURL} target="_blank" style={{ width: '28px', height: '28px' }} rel="noreferrer">
    <Avatar src={avatarURL} />
  </a>
)

interface HeaderProps {
  title: string
  artist: {
    name: string
    url: string
    avatar: string
  }
  color?: string
}

const AvatarBox = styled('div', {
  width: '40px',
  height: '40px',
  minWidth: '40px',
  minHeight: '40px',
  borderRadius: '8px',
  boxShadow: '1px 1px 10px -4px #00000026 inset',
  background: '$gray02',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
})

const HeaderContent: FC<HeaderProps> = ({ title, artist: { name, url, avatar }, color }) => {
  const logoColor = color && (MINTABLE_COLORS_MAP[color]?.LIGHT || `${color}46`)
  return (
    <>
      <Row css={{ alignItems: 'center', overflow: 'hidden' }}>
        <AvatarBox>
          <LinkAvatar avatarURL={avatar} artistURL={url} />
        </AvatarBox>
        <Box css={{ marginLeft: '12px', overflow: 'hidden' }}>
          <Text
            size={'12'}
            css={{
              lineHeight: '16px',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis'
            }}
          >
            {title}
          </Text>
          <Text
            size={'12'}
            type={'secondary'}
            css={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
          >
            {name}
          </Text>
        </Box>
      </Row>
      <ArpeggiLightLogo fill={logoColor} style={{ margin: '5px' }} />
    </>
  )
}

export default HeaderContent
